import React, { useEffect, useMemo, useRef, useState } from "react";
import { printingApi } from "../../lib/printing/printingApi";
import {
  DOC_TYPES, DOC_TYPE_LABELS, TRIGGER_LABELS, ROLE_LABELS, JOB_STATES, jobStateSpec, reprintPolicyFor, reprintLabelFor,
  newRequestKey, filterJobs, auditTimeline, formatWhen, shortId, operatorMessage, errorCode, TRANSMITTED_EXPLANATION,
} from "../../lib/printing/printingModel";
import { SectionCard, Field, CheckField, inputCls, btnPrimary, btnSecondary, btnDanger, Modal, StateBadge, Pill, KeyValue, Notice, EmptyState, Loading } from "./PrintingUi";
import { TestPrintDialog } from "./PrintersPanel";

export function ReprintDialog({ open, onClose, job, onDone, notify }) {
  const policy = reprintPolicyFor(job);
  const label = reprintLabelFor(job?.doc_type);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // One stable key per dialog opening: a double-click or a retry after a
  // network blip replays the same reprint instead of creating a second one.
  const [requestKey] = useState(() => newRequestKey());
  const kitchen = job?.doc_type === "kitchen_ticket";

  const submit = async (e) => {
    e.preventDefault();
    if (busy || result) return;
    if (reason.trim().length < 3) { setError("Enter a reason (at least 3 characters). It is recorded in the audit log."); return; }
    if (policy.requires_acknowledgement && !ack) { setError("Tick the acknowledgement: this document may already have printed."); return; }
    setError(null); setBusy(true);
    try {
      const data = await printingApi.reprint(job.id, { reason: reason.trim(), acknowledge_duplicate_risk: policy.requires_acknowledgement ? ack : false, request_key: requestKey });
      setResult({ replayed: Boolean(data.replayed), job: data.job });
      notify("success", data.replayed ? "That reprint request was already queued — no second job was created." : `Reprint queued as a new job labelled ${label}.`);
      onDone(data.job);
    } catch (err) {
      const code = errorCode(err);
      setError(code === "duplicate_risk_unacknowledged" ? "The printer may already have produced this document. Tick the acknowledgement to reprint anyway." : operatorMessage(err));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} testId="reprint-dialog" title={`Reprint ${DOC_TYPE_LABELS[job?.doc_type] || "document"}`}
      description={`Creates a new job labelled ${label}. The original job is not changed.`}>
      {!policy.allowed ? (
        <div className="space-y-3">
          <Notice tone="warning" testId="reprint-blocked">{policy.message}</Notice>
          <div className="flex justify-end"><button type="button" className={btnSecondary} onClick={onClose}>Close</button></div>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-3">
          {error && <Notice tone="danger" testId="reprint-error">{error}</Notice>}
          <Notice tone="info">The printout will carry the label <strong>{label}</strong>.{kitchen && " Kitchen reprints may cause the order to be prepared twice — tell the kitchen."}</Notice>
          {policy.requires_acknowledgement && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3" data-testid="reprint-ambiguous-warning">
              <p className="text-sm font-semibold text-red-900">Outcome of the original job is unknown</p>
              <p className="text-xs text-red-900">{policy.message} Check the paper before continuing.</p>
              <CheckField label="I understand this document may already have printed and I still want a reprint." checked={ack} onChange={setAck} testId="reprint-ack" />
            </div>
          )}
          <Field label="Reason" required hint="Recorded in the audit log with your user id.">
            <textarea className={`${inputCls} h-20 py-2`} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reprint-reason" maxLength={200} />
          </Field>
          {result && <Notice tone={result.replayed ? "warning" : "success"} testId="reprint-result">{result.replayed ? "Replayed: the backend returned the reprint job that already existed for this request." : `New reprint job ${shortId(result.job?.id)} queued.`}</Notice>}
          <div className="flex justify-end gap-2">
            <button type="button" className={btnSecondary} onClick={onClose}>{result ? "Close" : "Cancel"}</button>
            <button type="submit" className={btnPrimary} disabled={busy || Boolean(result)} data-testid="reprint-confirm">{busy ? "Queuing…" : "Queue reprint"}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export function JobDetailDialog({ open, onClose, jobId, printers, agents, isAdmin, onReprint, onCancel }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showText, setShowText] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    printingApi.job(jobId).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(operatorMessage(e)); });
    return () => { alive = false; };
  }, [jobId]);
  const job = data?.job;
  const policy = job ? reprintPolicyFor(job) : null;
  const printer = printers.find((p) => p.id === job?.printer_id);
  const agent = agents.find((a) => a.id === job?.claimed_by_agent_id);
  return (
    <Modal open={open} onClose={onClose} wide testId="job-detail-dialog" title={job ? `Job ${shortId(job.id)} — ${DOC_TYPE_LABELS[job.doc_type] || job.doc_type}` : "Job details"}>
      {error && <Notice tone="danger">{error}</Notice>}
      {!job && !error && <Loading label="Loading job…" />}
      {job && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2"><StateBadge state={job.state} />{job.duplicate_risk && <Pill tone="danger" testId="job-duplicate-risk">Duplicate risk acknowledged</Pill>}{job.original_print_job_id && <Pill tone="info">Reprint of {shortId(job.original_print_job_id)}</Pill>}{job.snapshot?.is_test && <Pill tone="warning">TEST — NOT A CUSTOMER ORDER</Pill>}</div>
          <KeyValue items={[
            ["Job ID", job.id], ["Order", job.snapshot?.order_code || job.order_id || "—"],
            ["Trigger", TRIGGER_LABELS[job.trigger_event] || job.trigger_event || "—"], ["Printer", printer?.name || job.printer_id || "not assigned"],
            ["Printer role", ROLE_LABELS[job.printer_role] || job.printer_role || "—"], ["Agent", agent?.name || job.claimed_by_agent_id || "—"],
            ["Created", formatWhen(job.created_at)], ["Transmitted", formatWhen(job.transmitted_at || job.completed_at)],
            ["Attempts", `${job.attempt_count ?? 0} of ${job.max_attempts ?? "?"}`], ["Copies", job.copies ?? job.number_of_copies ?? 1],
            ["Layout", job.layout_id ? `${job.layout_id.startsWith("system:") ? "System" : "Custom"} v${job.layout_version ?? "?"}` : "—"], ["Rule", job.rule_id ? `${job.rule_id.startsWith("system:") ? "System default" : shortId(job.rule_id)} v${job.rule_version ?? "?"}` : "—"],
            ["Checksum", job.rendered_document_checksum ? `${job.rendered_document_checksum.slice(0, 16)}…` : "—"], ["Bytes written", job.bytes_written ?? "—"],
            ["Last error", job.error_code ? `${job.error_code}${job.failure_reason ? ` — ${job.failure_reason}` : ""}` : "none"], ["Reprint label", job.reprint_label || "—"],
          ]} />
          <div>
            <h4 className="text-sm font-semibold">Audit timeline</h4>
            <ol className="mt-1 space-y-1 border-l-2 border-stone-300 pl-3" data-testid="audit-timeline">
              {auditTimeline(data.audit).map((s) => (
                <li key={s.id} className="text-sm" data-testid={`audit-${s.action}`}>
                  <span className="font-medium">{s.to ? jobStateSpec(s.to).label : s.action}</span>
                  <span className="text-xs text-stone-600"> · {s.action}{s.from ? ` (from ${jobStateSpec(s.from).label})` : ""} · {formatWhen(s.at)}{s.attempt != null ? ` · attempt ${s.attempt}` : ""}{s.actor ? ` · ${s.actor}` : ""}</span>
                </li>
              ))}
              {(!data.audit || data.audit.length === 0) && <li className="text-sm text-stone-500">No audit rows.</li>}
            </ol>
          </div>
          <p className="text-xs text-stone-600">{TRANSMITTED_EXPLANATION}</p>
          {job.text_preview && (
            <div>
              <button type="button" className={btnSecondary} onClick={() => setShowText((v) => !v)} aria-expanded={showText} data-testid="job-text-toggle">{showText ? "Hide printed text" : "Show printed text (as rendered for the agent)"}</button>
              {showText && <pre tabIndex={0} aria-label="Printed text" className="mt-1 max-h-64 overflow-auto rounded border border-stone-300 bg-stone-50 p-2 font-mono text-xs" data-testid="job-text-preview">{job.text_preview}</pre>}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {isAdmin && <button type="button" className={policy?.allowed ? btnPrimary : btnSecondary} disabled={!policy?.allowed} title={policy?.allowed ? undefined : policy?.message} onClick={() => onReprint(job)} data-testid="job-reprint-btn">Reprint{policy?.requires_acknowledgement ? " (needs acknowledgement)" : ""}</button>}
            {isAdmin && job.state === "queued" && <button type="button" className={btnDanger} onClick={() => onCancel(job)} data-testid="job-cancel-btn">Cancel job</button>}
            {policy && !policy.allowed && <span className="text-xs text-stone-600 self-center" data-testid="job-reprint-policy">{policy.message}</span>}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Cancel-confirmation for a queued print job. Built on the printing
 * `Modal` (focus trap, Escape, restore) rather than the generic
 * ConfirmModal so it is always the top-most and only interactive layer:
 * the caller closes the Details dialog before opening this one, and focus
 * is handed back to the job row's Details button when it closes.
 */
export function CancelJobDialog({ job, printers, busy, onKeep, onConfirm }) {
  const printer = printers.find((p) => p.id === job.printer_id);
  const orderLabel = job.snapshot?.order_code || job.order_id || "—";
  return (
    <Modal open onClose={onKeep} testId="job-cancel-confirm-modal" title={`Cancel print job ${shortId(job.id)}?`}
      description="Only this print job is cancelled. The order itself is not changed, refunded or cancelled.">
      <div className="space-y-3">
        <KeyValue items={[
          ["Document", DOC_TYPE_LABELS[job.doc_type] || job.doc_type], ["Order", orderLabel],
          ["Printer", printer?.name || job.printer_id || "not assigned"], ["State", jobStateSpec(job.state).label],
        ]} />
        <p className="text-sm text-stone-700">The job will not be printed. Only queued jobs can be cancelled; anything already claimed by the agent runs to a final outcome.</p>
        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onKeep} disabled={busy} data-testid="job-cancel-confirm-cancel">Keep job</button>
          <button type="button" className={btnDanger} onClick={onConfirm} disabled={busy} aria-busy={busy} data-testid="job-cancel-confirm-accept">{busy ? "Cancelling…" : "Cancel job"}</button>
        </div>
      </div>
    </Modal>
  );
}

export default function JobsPanel({ jobs, printers, agents, isAdmin, initialFilter, onChanged, notify }) {
  const [filter, setFilter] = useState({ state: "", docType: "", printerId: "", order: "", dateFrom: "", dateTo: "", ambiguousOnly: false, ...(initialFilter || {}) });
  const [detailId, setDetailId] = useState(null);
  const [reprintJob, setReprintJob] = useState(null);
  const [cancelJob, setCancelJob] = useState(null);
  const [testPrint, setTestPrint] = useState(false);
  const [busy, setBusy] = useState(false);
  const cancelInFlight = useRef(false);
  const cancelReturnFocusId = useRef(null);
  useEffect(() => { if (initialFilter) setFilter((f) => ({ ...f, ...initialFilter })); }, [initialFilter]);
  const rows = useMemo(() => filterJobs(jobs, filter), [jobs, filter]);
  const setF = (k) => (e) => setFilter((f) => ({ ...f, [k]: e.target.value }));
  const printerName = (id) => printers.find((p) => p.id === id)?.name || (id ? shortId(id) : "—");

  // Opening the confirmation replaces the Details dialog (one modal layer
  // at a time) and remembers where focus should land afterwards.
  const askCancel = (job) => {
    cancelReturnFocusId.current = `job-open-${job.id}`;
    setDetailId(null);
    setCancelJob(job);
  };
  const closeCancel = () => {
    setCancelJob(null);
    const id = cancelReturnFocusId.current;
    cancelReturnFocusId.current = null;
    if (id) setTimeout(() => { document.getElementById(id)?.focus(); }, 0);
  };
  const doCancel = async () => {
    if (cancelInFlight.current) return;   // a second click never sends a second request
    cancelInFlight.current = true;
    setBusy(true);
    try { await printingApi.cancelJob(cancelJob.id, "cancelled from dashboard"); notify("success", "Job cancelled."); closeCancel(); onChanged(); }
    catch (err) { notify("error", operatorMessage(err)); }
    finally { cancelInFlight.current = false; setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="jobs-panel">
      <SectionCard title="Print jobs and audit history" description={TRANSMITTED_EXPLANATION}
        actions={isAdmin && <button type="button" className={btnPrimary} onClick={() => setTestPrint(true)} data-testid="jobs-test-print-btn">Queue test print</button>}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Field label="State"><select className={inputCls} value={filter.state} onChange={setF("state")} data-testid="filter-state"><option value="">All</option>{Object.entries(JOB_STATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></Field>
          <Field label="Document"><select className={inputCls} value={filter.docType} onChange={setF("docType")} data-testid="filter-doctype"><option value="">All</option>{[...DOC_TYPES, "order_confirmation"].map((d) => <option key={d} value={d}>{DOC_TYPE_LABELS[d]}</option>)}</select></Field>
          <Field label="Printer"><select className={inputCls} value={filter.printerId} onChange={setF("printerId")} data-testid="filter-printer"><option value="">All</option>{printers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="Order"><input className={inputCls} value={filter.order} onChange={setF("order")} data-testid="filter-order" placeholder="Order code or id" /></Field>
          <Field label="From (date)"><input className={inputCls} type="date" value={filter.dateFrom} onChange={setF("dateFrom")} data-testid="filter-from" /></Field>
          <Field label="To (date)"><input className={inputCls} type="date" value={filter.dateTo} onChange={setF("dateTo")} data-testid="filter-to" /></Field>
        </div>
        <CheckField label="Needs attention only (unknown outcome)" checked={filter.ambiguousOnly} onChange={(v) => setFilter((f) => ({ ...f, ambiguousOnly: v }))} testId="filter-ambiguous" />
        <p className="text-xs text-stone-600" data-testid="jobs-count">{rows.length} of {jobs.length} recent jobs shown.</p>
        {rows.length === 0 ? <EmptyState testId="jobs-empty">No jobs match. Queue a test print to see the flow end to end.</EmptyState> : (
          <ul className="space-y-2" data-testid="jobs-list">
            {rows.map((j) => {
              const policy = reprintPolicyFor(j);
              return (
                <li key={j.id} className="grid grid-cols-1 gap-2 rounded-md border border-stone-200 p-3 md:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_auto] md:items-center" data-testid={`job-row-${j.id}`}>
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2"><StateBadge state={j.state} />{j.snapshot?.is_test && <Pill tone="warning">TEST</Pill>}{j.original_print_job_id && <Pill tone="info">{j.reprint_label || "Reprint"}</Pill>}{j.duplicate_risk && <Pill tone="danger">Duplicate risk</Pill>}</div>
                    <div className="text-sm font-medium">{DOC_TYPE_LABELS[j.doc_type] || j.doc_type} · order {j.snapshot?.order_code || shortId(j.order_id)}</div>
                    <div className="text-xs text-stone-600">Job {shortId(j.id)} · {TRIGGER_LABELS[j.trigger_event] || j.trigger_event || "—"} · created {formatWhen(j.created_at)}</div>
                  </div>
                  <div className="text-xs text-stone-700">
                    <div>Printer: {printerName(j.printer_id)} ({ROLE_LABELS[j.printer_role] || j.printer_role || "—"})</div>
                    <div>Attempts {j.attempt_count ?? 0}/{j.max_attempts ?? "?"} · copies {j.copies ?? 1}{j.transmitted_at ? ` · transmitted ${formatWhen(j.transmitted_at)}` : ""}</div>
                    {j.error_code && <div className="text-red-900">Last error: {j.error_code}</div>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" id={`job-open-${j.id}`} className={btnSecondary} onClick={() => setDetailId(j.id)} data-testid={`job-open-${j.id}`}>Details</button>
                    {isAdmin && <button type="button" className={btnSecondary} disabled={!policy.allowed} title={policy.allowed ? undefined : policy.message} onClick={() => setReprintJob(j)} data-testid={`reprint-open-${j.id}`}>Reprint</button>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {detailId && <JobDetailDialog open onClose={() => setDetailId(null)} jobId={detailId} printers={printers} agents={agents} isAdmin={isAdmin}
        onReprint={(job) => { setReprintJob(job); }} onCancel={askCancel} />}
      {reprintJob && <ReprintDialog key={reprintJob.id} open onClose={() => setReprintJob(null)} job={reprintJob} notify={notify} onDone={() => onChanged()} />}
      {testPrint && <TestPrintDialog open onClose={() => setTestPrint(false)} printers={printers} onQueued={() => onChanged()} notify={notify} />}
      {cancelJob && <CancelJobDialog job={cancelJob} printers={printers} busy={busy} onKeep={closeCancel} onConfirm={doCancel} />}
    </div>
  );
}
